/**
 * Per-client Content Calendar — month grid of scheduled + published FB/IG
 * posts, drag-to-reschedule (PATCH scheduled_at), day side-panel with
 * cancel/delete, and "+ Plan post" deep link into the Post Composer.
 * Read/write layer over db.social_posts ONLY — Piece D scheduler untouched.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { socialAPI, clientGroupAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Loader2, ChevronLeft, ChevronRight, Facebook, Instagram, CalendarDays,
  Plus, CalendarX, Trash2, History, Film, Camera, PenSquare,
} from 'lucide-react';
import { toast } from 'sonner';

const TYPE_ICON = { feed: PenSquare, story: Camera, reel: Film };
const localDateKey = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const todayKey = () => localDateKey(new Date().toISOString());

function PostChip({ post, onClick }) {
  const Icon = TYPE_ICON[post.post_type] || PenSquare;
  const plats = new Set();
  (post.targets || []).forEach((t) => (t.platforms || []).forEach((p) => plats.add(p)));
  const cls = post.status === 'scheduled' ? 'border-blue-500/50 bg-blue-500/10 text-blue-200'
    : post.status === 'published' ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
    : post.status === 'failed' ? 'border-red-500/40 bg-red-500/10 text-red-200'
    : 'border-border/40 bg-foreground/5 text-foreground/70';
  return (
    <button type="button"
            draggable={post.status === 'scheduled'}
            onDragStart={(e) => e.dataTransfer.setData('text/plain', post.id)}
            onClick={onClick}
            className={`w-full text-left rounded border px-1.5 py-0.5 text-[10px] truncate flex items-center gap-1 ${cls} ${post.status === 'scheduled' ? 'cursor-grab' : ''}`}
            title={post.caption || ''}
            data-testid={`cal-chip-${post.id}`}>
      <Icon className="h-2.5 w-2.5 shrink-0" />
      {plats.has('facebook') && <Facebook className="h-2.5 w-2.5 shrink-0 text-[#4d94ff]" />}
      {plats.has('instagram') && <Instagram className="h-2.5 w-2.5 shrink-0 text-pink-300" />}
      <span className="truncate">{post.caption || '(no caption)'}</span>
    </button>
  );
}

export default function ClientContentCalendarPage() {
  const { clientGroupId } = useParams();
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.id;

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-based
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState('');
  const [selectedDay, setSelectedDay] = useState(todayKey());
  const [busyId, setBusyId] = useState(null);

  const fetchPosts = useCallback(async ({ silent = false } = {}) => {
    if (!wsId || !clientGroupId) return;
    if (!silent) setLoading(true);
    try {
      const r = await socialAPI.listPosts(wsId, { client_group_id: clientGroupId, limit: 200 });
      setPosts(r?.data?.items || []);
    } catch { /* noop */ } finally {
      if (!silent) setLoading(false);
    }
  }, [wsId, clientGroupId]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);
  useEffect(() => {
    if (!wsId) return;
    clientGroupAPI.list(wsId)
      .then((r) => setClientName(((r?.data || []).find((c) => c.id === clientGroupId) || {}).name || ''))
      .catch(() => {});
  }, [wsId, clientGroupId]);

  // date-key → posts (scheduled use scheduled_at; published use publish_finished_at)
  const byDay = useMemo(() => {
    const m = {};
    posts.forEach((p) => {
      if (p.status === 'draft') return;
      const key = localDateKey(p.status === 'scheduled' || p.status === 'publishing'
        ? p.scheduled_at || p.created_at
        : p.publish_finished_at || p.scheduled_at || p.created_at);
      if (!key) return;
      (m[key] = m[key] || []).push(p);
    });
    return m;
  }, [posts]);

  // Month grid cells (weeks start Monday).
  const cells = useMemo(() => {
    const first = new Date(year, month, 1);
    const lead = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const out = [];
    for (let i = 0; i < lead; i += 1) out.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) {
      out.push(`${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
    }
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [year, month]);

  const shiftMonth = (delta) => {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
  };

  const onDropOnDay = async (e, dayKey) => {
    e.preventDefault();
    const postId = e.dataTransfer.getData('text/plain');
    const post = posts.find((p) => p.id === postId);
    if (!post || post.status !== 'scheduled' || !dayKey) return;
    // Keep the original time-of-day; move the date.
    const old = new Date(post.scheduled_at);
    const [y, m, d] = dayKey.split('-').map(Number);
    const next = new Date(y, m - 1, d, old.getHours(), old.getMinutes(), 0, 0);
    if (next.getTime() < Date.now() - 60000) {
      toast.error('Cannot reschedule into the past');
      return;
    }
    setBusyId(postId);
    try {
      await socialAPI.patchPost(wsId, postId, { scheduled_at: next.toISOString() });
      toast.success(`Rescheduled to ${next.toLocaleString()}`);
      await fetchPosts({ silent: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reschedule failed');
    } finally {
      setBusyId(null);
    }
  };

  const cancelSchedule = async (post) => {
    setBusyId(post.id);
    try {
      await socialAPI.cancelSchedule(wsId, post.id);
      toast.success('Schedule cancelled — back to draft');
      await fetchPosts({ silent: true });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Cancel failed'); } finally { setBusyId(null); }
  };

  const deletePost = async (post) => {
    setBusyId(post.id);
    try {
      await socialAPI.deletePost(wsId, post.id);
      toast.success('Post deleted');
      await fetchPosts({ silent: true });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); } finally { setBusyId(null); }
  };

  const dayPosts = byDay[selectedDay] || [];
  const monthLabel = new Date(year, month, 1).toLocaleString(undefined, { month: 'long', year: 'numeric' });

  return (
    <div className="p-6 space-y-4 animate-fade-in-up" data-testid="client-content-calendar-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-gold" /> {clientName || 'Client'} — Content Calendar
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Plan, schedule, and reschedule FB + IG content. Drag a scheduled post to another day to move it.
          </p>
        </div>
        <Button size="sm" variant="outline" className="h-8 text-[11px]"
                onClick={() => navigate(`/clients/${clientGroupId}/social-timeline`)}
                data-testid="calendar-goto-timeline">
          <History className="h-3.5 w-3.5 mr-1" /> Timeline
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* ── Month grid ── */}
        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-2">
            <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => shiftMonth(-1)} data-testid="calendar-prev-month">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium" data-testid="calendar-month-label">{monthLabel}</span>
            <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => shiftMonth(1)} data-testid="calendar-next-month">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gold/50" /></div>
          ) : (
            <div className="border border-border/30 rounded-lg overflow-hidden">
              <div className="grid grid-cols-7 bg-foreground/[0.03] border-b border-border/30">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                  <div key={d} className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground text-center">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {cells.map((key, i) => (
                  <div key={key || `x${i}`}
                       onClick={() => key && setSelectedDay(key)}
                       onDragOver={(e) => key && e.preventDefault()}
                       onDrop={(e) => key && onDropOnDay(e, key)}
                       className={`min-h-[92px] border-b border-r border-border/20 p-1 space-y-0.5 ${key ? 'cursor-pointer hover:bg-foreground/[0.02]' : 'bg-foreground/[0.015]'} ${key === selectedDay ? 'ring-1 ring-inset ring-gold/50 bg-gold/[0.04]' : ''}`}
                       data-testid={key ? `cal-day-${key}` : undefined}>
                    {key && (
                      <>
                        <div className={`text-[10px] ${key === todayKey() ? 'text-gold font-semibold' : 'text-muted-foreground'}`}>
                          {Number(key.slice(-2))}
                        </div>
                        {(byDay[key] || []).slice(0, 3).map((p) => (
                          <PostChip key={p.id} post={p} onClick={() => setSelectedDay(key)} />
                        ))}
                        {(byDay[key] || []).length > 3 && (
                          <div className="text-[9px] text-muted-foreground pl-1">+{(byDay[key] || []).length - 3} more</div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-blue-400 inline-block" /> Scheduled</span>
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-400 inline-block" /> Published</span>
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-400 inline-block" /> Failed</span>
          </div>
        </div>

        {/* ── Day panel ── */}
        <div className="space-y-2" data-testid="calendar-day-panel">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{selectedDay ? new Date(`${selectedDay}T12:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) : 'Pick a day'}</span>
            <Button size="sm" className="h-7 text-[11px]"
                    onClick={() => navigate(`/compose?client=${clientGroupId}&date=${selectedDay}`)}
                    data-testid="calendar-plan-post">
              <Plus className="h-3 w-3 mr-1" /> Plan post
            </Button>
          </div>
          {dayPosts.length === 0 ? (
            <p className="text-[11px] text-muted-foreground py-4" data-testid="calendar-day-empty">Nothing on this day.</p>
          ) : dayPosts.map((p) => (
            <div key={p.id} className="rounded-md border border-border/30 p-2.5 space-y-1.5" data-testid={`calendar-day-post-${p.id}`}>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Badge variant="outline" className="text-[9px] uppercase">{p.status}</Badge>
                <Badge variant="outline" className="text-[9px] uppercase">{p.post_type || 'feed'}</Badge>
                <span className="text-[10px] text-muted-foreground">
                  {new Date(p.status === 'scheduled' ? p.scheduled_at : (p.publish_finished_at || p.scheduled_at || p.created_at)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <p className="text-[11px] text-foreground/85 line-clamp-2">{p.caption || '(no caption)'}</p>
              <div className="flex items-center gap-1.5">
                {p.status === 'scheduled' && (
                  <Button size="sm" variant="outline" className="h-6 text-[10px]" disabled={busyId === p.id}
                          onClick={() => cancelSchedule(p)} data-testid={`calendar-cancel-${p.id}`}>
                    <CalendarX className="h-3 w-3 mr-0.5" /> Cancel
                  </Button>
                )}
                {['draft', 'scheduled', 'failed', 'partial'].includes(p.status) && (
                  <Button size="sm" variant="outline" className="h-6 text-[10px] text-red-300 border-red-500/30" disabled={busyId === p.id}
                          onClick={() => deletePost(p)} data-testid={`calendar-delete-${p.id}`}>
                    <Trash2 className="h-3 w-3 mr-0.5" /> Delete
                  </Button>
                )}
                <button type="button" onClick={() => navigate('/queue')}
                        className="text-[10px] text-gold hover:underline" data-testid={`calendar-open-queue-${p.id}`}>
                  Queue →
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
